'use client';

import type { Breadcrumb } from '@eliadmin/core/src/api/products/types/product-types';
import { BreadcrumbsSchema } from '@eliadmin/core/src/components/seo/breadcrumbs-schema';
import { BREADCRUMBS_CONFIG } from '@eliadmin/core/src/constants/breadcrumbs-config';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import {
  arePathsEqual,
  getFlatBreadcrumbs,
  getSlugFromPage,
} from '@eliadmin/core/src/utils/url-utils';
import Link from '@eliadmin/core/src/components/link';
import { usePathname } from 'next/navigation';
import { FiHome } from 'react-icons/fi';
import { MdArrowRight } from 'react-icons/md';
import { ButtonIcon } from '../ui/button-icon';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';

export type BreadcrumbsProps = {
  className?: {
    wrapper?: string;
    list?: string;
    item?: string;
    itemActive?: string;
    link?: string;
  };
  homeText?: string;
  breadcrumb: Breadcrumb;
  variant?: 'default' | 'secondary';
};

export function Breadcrumbs({ breadcrumb, className, variant = 'default' }: BreadcrumbsProps) {
  const { t } = useTranslations();
  const pathname = usePathname();

  const breadcrumbs = getFlatBreadcrumbs(breadcrumb);

  if (BREADCRUMBS_CONFIG.noDisplay.filter((el: string) => el === pathname).length) {
    return <nav className="hidden"></nav>;
  }

  return (
    <>
      <BreadcrumbsSchema breadcrumb={breadcrumb} />
      <nav
        className={cn(
          'hide-scroll-bar w-full overflow-x-scroll rounded-none bg-transparent',
          'ea-breadcrumbs',
          className?.wrapper,
        )}
      >
        <ol
          className={cn(
            'flex flex-row flex-nowrap items-center gap-0',
            'ea-breadcrumbs__list',
            className?.list,
          )}
        >
          <li
            className={cn(
              {
                [cn(
                  'pointer-events-none',
                  variant === 'secondary' ? 'text-white' : 'text-black',
                  'ea_breadcrumbs__item--active',
                  className?.itemActive,
                )]: arePathsEqual(pathname, getSlugFromPage('home')),
              },
              'ea_breadcrumbs__item',
              className?.item,
            )}
          >
            <Link
              href={getSlugFromPage('home') ?? '/'}
              className={cn('ea-breadcrumbs__link', className?.link)}
              prefetch={false}
              // ButtonIcon is aria-hidden, so the icon-only link needs its own name.
              aria-label={t('text160')}
            >
              <ButtonIcon
                alt="Domov"
                url={
                  variant === 'secondary'
                    ? '/images/icons/home-secondary.svg'
                    : '/images/icons/home-primary.svg'
                }
              />
            </Link>
          </li>
          {breadcrumbs.map((breadcrumb, index) => {
            const isActive = arePathsEqual(pathname, breadcrumb.slug);
            return (
              <li key={index} className="flex w-auto items-center justify-start whitespace-nowrap">
                <span className={cn(className?.item)}>
                  <MdArrowRight size={20} className="text-border" />
                </span>
                <span
                  className={cn(
                    {
                      [cn(
                        'pointer-events-none font-semibold',
                        variant === 'secondary' ? 'text-white' : 'text-black',
                        'ea_breadcrumbs__item--active',
                        className?.itemActive,
                      )]: isActive,
                      [cn(variant === 'secondary' ? 'text-secondary' : 'text-primary')]: !isActive,
                    },
                    'ea_breadcrumbs__item',
                    className?.item,
                  )}
                >
                  <Link
                    href={breadcrumb.slug ?? '#'}
                    className={cn('font-semibold', 'ea-breadcrumbs__link', className?.link)}
                    prefetch={false}
                  >
                    {breadcrumb.name}
                  </Link>
                </span>
              </li>
            );
          })}
        </ol>
      </nav>
    </>
  );
}
